import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { CardModule } from 'primeng/card';
import { InputIconModule } from 'primeng/inputicon';
import { MessageModule } from 'primeng/message';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

import { AuthService } from '../../services/auth.service';
import { LoginRequest } from '../../models/auth.models';
import { ThemeService } from '@shared/services/theme.service';
import { VersionService } from '@shared/services/version.service';
import { AppVersion } from '@shared/models/version.t';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    CardModule,
    InputIconModule,
    MessageModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private themeService = inject(ThemeService);
  private versionService = inject(VersionService);

  isDarkMode = this.themeService.getDarkMode();

  readonly isMaintenance = environment.maintenance;
  readonly maintenanceTime = this.formatMaintenanceEndTime(environment.maintenanceTime);

  loginForm: FormGroup;
  isLoading = signal<boolean>(false);

  private appVersion = signal<AppVersion | null>(null);

  /** Display-ready version info, or null when no version is available. */
  readonly versionInfo = computed(() => {
    const value = this.appVersion();
    if (!value?.version) {
      return null;
    }
    return {
      version: value.version,
      updatedAt: this.formatVersionDate(value.merged_at),
      daysAgo: this.daysSince(value.merged_at),
    };
  });

  constructor() {
    this.loginForm = this.fb.group({
      correo_electronico: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });

    if (this.isMaintenance) {
      this.loginForm.disable();
    }

    // Fetch the deployed version (non-critical: failures leave the badge hidden).
    this.versionService
      .getAppVersion()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: version => this.appVersion.set(version),
        error: () => this.appVersion.set(null),
      });

    // Subscribe to auth state changes
    this.authService.authState$.pipe(takeUntilDestroyed()).subscribe(state => {
      this.isLoading.set(state.isLoading);

      if (state.isAuthenticated && !state.isLoading) {
        this.redirectAfterLogin();
      }
    });
  }

  /**
   * Where to go after a successful login, in priority order:
   *  1. A safe `?next=` path — used by the nginx gateway to return the user to a
   *     non-SPA surface (e.g. /scheduler, /docs); needs a full-page navigation
   *     because those are not Angular routes.
   *  2. The stored returnUrl (an in-app route the auth guard saved).
   *  3. /home.
   */
  private redirectAfterLogin(): void {
    const next = this.sanitizeNext(this.route.snapshot.queryParamMap.get('next'));
    if (next) {
      this.navigateExternal(next);
      return;
    }

    const returnUrl = this.authService.getReturnUrl();
    if (returnUrl) {
      this.authService.clearReturnUrl();
      this.router.navigateByUrl(returnUrl);
      return;
    }

    this.router.navigate(['/home']);
  }

  /**
   * Returns a same-origin absolute path safe to redirect to, or null. Guards
   * against open redirects: only `/path` is allowed (no `//host`, no `/\host`,
   * no absolute URLs).
   */
  protected sanitizeNext(next: string | null): string | null {
    if (!next || !next.startsWith('/')) return null;
    if (next.startsWith('//') || next.startsWith('/\\')) return null;
    return next;
  }

  /** Full-page navigation (for non-SPA surfaces served by nginx). */
  protected navigateExternal(url: string): void {
    window.location.assign(url);
  }

  /**
   * Handle form submission
   */
  onSubmit(): void {
    if (this.isMaintenance) {
      return;
    }
    if (this.loginForm.valid) {
      const credentials: LoginRequest = this.loginForm.value;

      this.authService.login(credentials).subscribe({
        next: success => {
          if (success) {
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: 'Inicio de sesión exitoso',
            });
          }
        },
        error: error => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error || 'Error al iniciar sesión',
          });
        },
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  /**
   * Mark all form controls as touched to trigger validation display
   */
  private markFormGroupTouched(): void {
    Object.keys(this.loginForm.controls).forEach(key => {
      const control = this.loginForm.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Check if form field is invalid and touched
   */
  isFieldInvalid(fieldName: string): boolean {
    const field = this.loginForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  /**
   * Get error message for form field
   */
  getErrorMessage(fieldName: string): string {
    const field = this.loginForm.get(fieldName);

    if (!field || !field.errors) return '';

    if (field.errors['required']) {
      return 'Este campo es requerido';
    }

    if (field.errors['email']) {
      return 'Ingrese un email válido';
    }

    if (field.errors['minlength']) {
      return `Mínimo ${field.errors['minlength'].requiredLength} caracteres`;
    }

    return '';
  }

  getSubmitBtnIcon(): string {
    if (this.isLoading()) {
      return 'pi pi-spinner pi-spin';
    }
    return 'pi pi-sign-in';
  }

  /**
   * Formats maintenance end time for display in Colombia timezone (es-CO).
   */
  private formatMaintenanceEndTime(iso: string): string {
    if (!iso) {
      return '';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleString('es-CO', {
      timeZone: 'America/Bogota',
      dateStyle: 'long',
      timeStyle: 'short',
    });
  }

  /**
   * Formats an ISO timestamp as dd/mm/yyyy, or '' when missing/invalid.
   */
  private formatVersionDate(iso: string | null): string {
    if (!iso) {
      return '';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${date.getFullYear()}`;
  }

  /**
   * Whole days elapsed since the given ISO timestamp, or null when missing/invalid.
   */
  private daysSince(iso: string | null): number | null {
    if (!iso) {
      return null;
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    const diffMs = Date.now() - date.getTime();
    return Math.max(0, Math.floor(diffMs / 86_400_000));
  }
}
